import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Database } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useReference } from "@/context/ReferenceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "carriers", label: "Carriers" },
  { value: "products", label: "Products" },
  { value: "states", label: "Licensed States" },
  { value: "sources", label: "Lead Sources" },
  { value: "vendors", label: "Vendors" },
];

export function ReferenceManager() {
  const { refresh } = useReference();
  const [type, setType] = useState("carriers");
  const [all, setAll] = useState([]);
  const [newVal, setNewVal] = useState("");

  const load = () => api.get("/reference").then(({ data }) => setAll(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const items = useMemo(
    () => all.filter((r) => r.type === type).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [all, type]
  );

  const sync = async () => { await load(); refresh(); };

  const add = async () => {
    const v = newVal.trim();
    if (!v) return;
    try {
      await api.post("/reference", { type, value: v });
      setNewVal("");
      toast.success("Value added");
      sync();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggle = async (r) => {
    await api.put(`/reference/${r.id}`, { active: !(r.active !== false) }).catch(() => {});
    sync();
  };

  const rename = async (r, value) => {
    if (!value.trim() || value === r.value) return;
    await api.put(`/reference/${r.id}`, { value: value.trim() }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    sync();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.value}"? Historical records keep their existing value.`)) return;
    await api.delete(`/reference/${r.id}`).catch(() => {});
    toast.success("Value removed");
    sync();
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.post("/reference/reorder", { type, ordered_ids: arr.map((r) => r.id) }).catch(() => {});
    sync();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="reference-manager">
      <div className="flex items-center gap-2 mb-1">
        <Database className="h-5 w-5 text-primary" />
        <h3 className="font-heading text-base font-semibold">Reference Data</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Add, edit, deactivate and reorder dropdown values used across the app. Deactivating hides a value from new records without altering historical data.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48" data-testid="ref-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input
          data-testid="ref-new-value"
          placeholder={`New ${type} value…`}
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          className="w-56"
        />
        <Button onClick={add} data-testid="ref-add-btn"><Plus className="mr-1 h-4 w-4" /> Add</Button>
      </div>

      <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
        {items.map((r, idx) => (
          <div key={r.id} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5" data-testid={`ref-row-${r.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} className="text-muted-foreground hover:text-foreground" disabled={idx === 0}><ChevronUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(idx, 1)} className="text-muted-foreground hover:text-foreground" disabled={idx === items.length - 1}><ChevronDown className="h-3.5 w-3.5" /></button>
            </div>
            <input
              defaultValue={r.value}
              onBlur={(e) => rename(r, e.target.value)}
              className={`flex-1 bg-transparent text-sm outline-none ${r.active === false ? "text-muted-foreground line-through" : ""}`}
            />
            <Switch checked={r.active !== false} onCheckedChange={() => toggle(r)} title="Active" />
            <button onClick={() => remove(r)} className="rounded p-1 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">No values yet.</div>}
      </div>
    </div>
  );
}
